/**
 * Templates — which pieces of configured work are consuming the allowance.
 *
 * Fed by /dashboard/trend, which returns per-template daily request counts for
 * the period. That is real, measured volume. The cost column beside it is not
 * measured: nothing records consumption per template, so it is this
 * organisation's cycle consumption split by each template's share of requests.
 * The column is labelled "allocated" everywhere it appears, and the note below
 * the table says it in full — an estimate presented as a measurement is how
 * chargeback arguments start.
 */
import { useMemo, useState } from 'react'
import { AlertCircle, ArrowDown, ArrowUp, Layers } from 'lucide-react'
import { EmptyState, ShareBar, Sparkline } from './BillingPrimitives'
import { allocateByShare, bucketSeries, formatInt, formatUsdAs } from '../../utils/billing'
import { isAllTimeLabel } from './useBillingData'

export default function TemplatesPanel({ trend, consumption, chart, periodLabel, onSelect, displayCurrency }) {
  const [sort, setSort] = useState({ key: 'total', dir: 'desc' })

  const rows = useMemo(() => {
    const series = trend?.data?.series || []
    const allocated = allocateByShare(series.filter(s => s.total > 0), consumption)
    return [...allocated].sort((a, b) => {
      const diff = (b[sort.key] || 0) - (a[sort.key] || 0)
      return sort.dir === 'desc' ? diff : -diff
    })
  }, [trend, consumption, sort])

  const max = Math.max(1, ...rows.map(r => r.total))
  const totalRequests = rows.reduce((sum, r) => sum + r.total, 0)

  const toggleSort = key =>
    setSort(s => (s.key === key ? { key, dir: s.dir === 'desc' ? 'asc' : 'desc' } : { key, dir: 'desc' }))

  // A full skeleton only ever replaces an empty page — once rows exist, a
  // period switch dims them instead. Swapping a real table for a skeleton on
  // every filter change was flagged as "the filter doesn't seem to do
  // anything": against real network latency the stale rows just sat there
  // with no visible sign a refetch was in flight, and only the caption text
  // (silently) changed underneath them a few seconds later.
  const initialLoad = trend?.loading && !trend?.data
  const refreshing = trend?.loading && !!trend?.data

  if (initialLoad) {
    return (
      <div className="p-5 space-y-2">
        {[0, 1, 2, 3, 4].map(i => <div key={i} className="skeleton h-11 rounded-lg" />)}
        <span className="sr-only" role="status">Loading template usage</span>
      </div>
    )
  }

  if (trend?.error) {
    return (
      <EmptyState
        icon={AlertCircle}
        title="Template usage is unavailable"
        body="The per-template breakdown comes from the activity data rather than billing, and that query did not return. Your plan and cost figures above are unaffected."
      />
    )
  }

  if (rows.length === 0) {
    return (
      <EmptyState
        icon={Layers}
        title={`No template activity ${isAllTimeLabel(periodLabel) ? 'yet' : `in the last ${periodLabel}`}`}
        body="Requests raised against a template appear here, ranked by volume."
      />
    )
  }

  return (
    <div className={`p-5 transition-opacity duration-200 ${refreshing ? 'opacity-50' : 'opacity-100'}`}>
      {refreshing && <span className="sr-only" role="status">Updating for the new period</span>}
      <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
        <div>
          <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Usage by template</h2>
          <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
            {rows.length} active {rows.length === 1 ? 'template' : 'templates'} · {formatInt(totalRequests)} requests
            {isAllTimeLabel(periodLabel) ? ', all time' : ` in the last ${periodLabel}`}
          </p>
        </div>
      </div>

      <div data-tour="templates-table" className="overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]">
          <thead>
            <tr className="border-b border-gray-200 dark:border-gray-800">
              <th className="text-left text-[10px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500 py-2 pr-3">
                Template
              </th>
              <th className="text-left text-[10px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500 py-2 px-3 w-[120px]">
                Trend
              </th>
              {[
                { key: 'total', label: 'Requests' },
                { key: 'allocated', label: 'Allocated cost' },
              ].map(col => (
                <th key={col.key} className="py-2 px-3 text-right">
                  <button
                    type="button"
                    onClick={() => toggleSort(col.key)}
                    aria-sort={sort.key === col.key ? (sort.dir === 'desc' ? 'descending' : 'ascending') : 'none'}
                    className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider
                               text-gray-400 dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-300
                               transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-navy-500 rounded"
                  >
                    {col.label}
                    {sort.key === col.key
                      ? (sort.dir === 'desc' ? <ArrowDown className="w-3 h-3" /> : <ArrowUp className="w-3 h-3" />)
                      : <span className="w-3" />}
                  </button>
                </th>
              ))}
              <th className="text-right text-[10px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500 py-2 pl-3 w-[170px]">
                Share
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr
                key={row.template_id ?? row.template_name}
                onClick={() => onSelect?.(row)}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect?.(row) }
                }}
                tabIndex={0}
                role="button"
                aria-label={`View details for ${row.template_name}`}
                className="border-b border-gray-100 dark:border-gray-800/60 last:border-0 cursor-pointer
                           hover:bg-gray-50 dark:hover:bg-gray-800/40 transition-colors animate-line-in
                           focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-navy-500"
                style={{ animationDelay: `${i * 26}ms` }}
              >
                <td className="py-2.5 pr-3">
                  <p className="font-medium text-gray-900 dark:text-gray-100 truncate text-[13px]">
                    {row.template_name}
                  </p>
                  <p className="text-[11px] text-gray-400 dark:text-gray-500">
                    {row.template_id ? `Template #${row.template_id}` : 'No template'}
                  </p>
                </td>
                <td className="py-2.5 px-3">
                  <Sparkline data={bucketSeries(row.counts, 20).values} color={chart.series[0]} height={22} />
                </td>
                <td className="py-2.5 px-3 text-right tabular-nums font-semibold text-gray-900 dark:text-gray-100">
                  {formatInt(row.total)}
                </td>
                <td className="py-2.5 px-3 text-right tabular-nums text-gray-600 dark:text-gray-300">
                  {formatUsdAs(row.allocated, displayCurrency)}
                </td>
                <td className="py-2.5 pl-3">
                  <ShareBar value={row.total} max={max} label={`${row.sharePct}%`} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="mt-4 pt-3 border-t border-gray-100 dark:border-gray-800 text-[11px] text-gray-500 dark:text-gray-400">
        Request counts are measured {isAllTimeLabel(periodLabel) ? 'over all time' : `over the last ${periodLabel}`}.
        <strong className="font-semibold"> Allocated cost is not measured.</strong> Consumption is recorded per
        processing job, never per template. It is <em>this billing month's</em> estimated AI processing charge
        ({formatUsdAs(consumption, displayCurrency)}) divided between templates by their share of requests in the selected period, so a
        period longer than the billing month spreads that month's cost using a longer-run average. Read it as where spend
        concentrates, not as a bill.
      </p>
    </div>
  )
}
